<template>
  <b-modal
    :id="`mc-modal-load-history-${activity?.id}`"
    ref="mc-modal-load-history"
    :title="title"
    class="mc-modal"
    size="xl"
    centered
    @show="handleModalShow"
  >
    <div class="card-body mc-table-container">
      <McTable
        ref="historyTable"
        :withCard="false"
        searchable
        stickyHeader
        :hasCheckbox="false"
        :hasSelectAll="false"
        :fields="fields"
        :apiClient="apiClient"
      >
        <template #beforeSearchBar>
          <div class="flex filter-container">
            <FormulateInput
              type="date-picker"
              format="DD MMM YYYY HH:mm"
              datepickerType="datetime"
              valueType="YYYY-MM-DDTHH:mm:ss.SSSZ"
              :confirmText="$t('global.action.apply')"
              name="completed_on"
              :confirm="true"
              :showSecond="false"
              :placeholder="$t('fleetTask.deliveryLoadHistory.placeholder.selectDate')"
              v-model="selectedDate"
              @input="handleDateChange"
            />
          </div>
        </template>
        <template #[`cell(created_on)`]="{ value }">
          {{ formatDateWithTz(value) }}
        </template>
        <template #[`cell(product)`]="{ value }">
          {{ value.name }}
          <div class="text-muted text-sm">
            {{ value.type }}
          </div>
        </template>
      </McTable>
    </div>

    <template #modal-footer>
      <div class="d-flex mx-0">
        <McButton
          variant="primary"
          class="me-2"
          :text="$t('global.action.close')"
          @click="handleModalCancel"
        />
      </div>
    </template>
  </b-modal>
</template>

<script>
import McButton from '@/components/McButton'
import McTable from '@/components/McTable'
import { formatDateWithTz } from '@/helpers/tzDate'
import * as historyApi from '@/services/rest-api/lib/delivery/history'
import filterMappings from '../lib/constants/filter-mappings'

import moment from 'moment'
import { formatNumber } from '@/helpers/format-number'
moment.locale('id')

export default {
  components: {
    McButton,
    McTable,
  },
  props: {
    activity: {
      type: Object,
      default: () => ({}),
    },
    fleetTaskId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      selectedDate: null,
      queryParamFilters: [],
    }
  },
  computed: {
    isPickUp() {
      return this.activity.type == 'PICKUP'
    },
    title() {
      return this.$t('fleetTask.deliveryLoadHistory.title', {
        activityLabel: this.activityLabel,
        deliveryNumber: this.activity.delivery_order?.number,
      })
    },
    activityLabel() {
      return this.$t(
        this.isPickUp
          ? 'fleetTask.deliveryLoadHistory.activity.pickup'
          : 'fleetTask.deliveryLoadHistory.activity.delivery'
      )
    },
    getEventFilterValue() {
      if (this.isPickUp) {
        return 'FleetPlanning.DeliveryLoadUpdatedPicked'
      } else {
        return 'FleetPlanning.DeliveryLoadUpdatedDropped'
      }
    },
    fields() {
      return [
        {
          label: this.$t('fleetTask.deliveryLoadHistory.fields.changeTime'),
          key: 'created_on',
          isSortable: false,
        },
        {
          label: this.$t('fleetTask.deliveryLoadHistory.fields.updatedBy'),
          key: 'created_by',
          isSortable: false,
        },
        {
          label: this.$t('fleetTask.deliveryLoadHistory.fields.productName'),
          key: 'product',
          isSortable: false,
        },
        {
          label: this.$t('fleetTask.deliveryLoadHistory.fields.oldQuantity'),
          key: 'old_quantity',
          isSortable: false,
        },
        {
          label: this.$t('fleetTask.deliveryLoadHistory.fields.newQuantity'),
          key: 'new_quantity',
          isSortable: false,
        },
        {
          label: this.$t('fleetTask.deliveryLoadHistory.fields.changeReason'),
          key: 'reason',
          isSortable: false,
        },
      ]
    },
    apiClient() {
      return {
        get: async (queryParam) => {
          const logResult = await historyApi.getLog(
            this.activity?.delivery_order.id,
            queryParam,
            this.queryParamFilters
          )

          const keyMap = {
            product_name: 'product_name',
            product_type_name: 'product_type_name',
            uom: 'uom',
            old_qty: 'old_quantity',
            new_qty: 'new_quantity',
            reason: 'reason',
          }

          const formatQty = (value) => {
            const num = parseFloat(value)
            if (isNaN(num)) return value

            // if decimal, show 3 decimal or more places
            return Number.isInteger(num)
              ? num.toString()
              : parseFloat(num.toFixed(3)).toString();
          }

          const remodeledLogData = logResult.data.paginated_result.map((log) => {
            const extractedData = {
              product_name: '',
              product_type_name: '',
              product: {
                name: '',
                type: '',
              },
              uom: '',
              old_quantity: '',
              new_quantity: '',
              reason: '',
              created_on: log.created_on,
              created_by: log.created_by,
            }

            log.details.forEach((item) => {
              if (keyMap[item.key]) {
                extractedData[keyMap[item.key]] = item.value
              }
            })
            extractedData.product = {
              name: extractedData.product_name,
              type: extractedData.product_type_name,
            }
            extractedData.old_quantity = `${formatNumber(parseFloat(extractedData.old_quantity))} ${
              extractedData.uom
            }`
            extractedData.new_quantity = `${formatNumber(parseFloat(extractedData.new_quantity))} ${
              extractedData.uom
            }`

            return extractedData
          })

          logResult.data.paginated_result = remodeledLogData

          return logResult
        },
      }
    },
  },
  methods: {
    formatDateWithTz,
    handleModalCancel() {
      this.$bvModal.hide(`mc-modal-load-history-${this.activity.id}`)
      this.activeAttachmentIdx = null
      this.selectedDate = null
    },
    updateQueryParamFilters() {
      const baseFilters = [
        {
          key: 'event',
          values: [this.getEventFilterValue],
        },
      ]

      if (this.selectedDate) {
        baseFilters.push({
          key: 'created_on:gte',
          values: [this.selectedDate],
        })

        const dateParts = this.selectedDate.split('T')

        const timeWithUpdatedSeconds =
          '23:59' + ':59.999' + dateParts[1].substring(dateParts[1].indexOf('.') + 4)

        const endDateString = dateParts[0] + 'T' + timeWithUpdatedSeconds

        baseFilters.push({
          key: 'created_on:lte',
          values: [endDateString],
        })
      }

      this.queryParamFilters = baseFilters.map(
        (filter) => filterMappings[filter.key]?.(filter.key, filter.values) ?? undefined
      )
    },
    handleDateChange(value) {
      this.selectedDate = value
      this.updateQueryParamFilters()

      if (this.$refs.historyTable) {
        this.$refs.historyTable.getData({
          limit: this.$refs.historyTable.limit,
          page: this.$refs.historyTable.page,
        })
      }
    },
    handleModalShow() {
      this.selectedDate = null
      this.updateQueryParamFilters()
    },
  },
}
</script>

<style scoped lang="scss">
.filter-container {
  position: relative;
  height: 0px;
  top: -3px;
  min-width: 260px;
}
.mc-table-container {
  :deep(.mc-has-search) {
    height: 38px !important;
  }
}
</style>
